import { clone } from "lodash";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";
import { v4 as uuid } from "uuid";

import { Stack } from "@seneca/senekit";

import sortDateDescending from "seneca-common/utils/functions/sorting/sortDateDescending";

import useFetchAmeliaSessions from "features/classroom/common/Amelia/hooks/useFetchAmeliaSessions";
import { ROUTES } from "routing/routes";

import {
  SidebarContentWrapperInner,
  SidebarContentWrapperOuter
} from "../common";
import LoadingSessions from "./LoadingSessions";
import NewChatItem from "./NewChatItem";
import Sessions, { groupByPeriod } from "./Sessions";

export default function Conversations({ courseId }: { courseId: string }) {
  const location = useLocation();

  const extractSessionIdRegex = new RegExp(/^.*?chat\/|(\/[a-z]+)?$/g);
  const activeSessionId = clone(location.pathname).replace(
    extractSessionIdRegex,
    ""
  );

  const { data: sessions, isLoading } = useFetchAmeliaSessions();

  const groupedSessions = useMemo(() => {
    const items =
      sessions?.items
        ?.filter(item => item.courseId === courseId && !!item.title)
        .sort((a, b) => sortDateDescending(a.timestamp, b.timestamp)) ?? [];
    return groupByPeriod(items);
  }, [courseId, sessions]);

  const navigate = useNavigate();

  const { today, lastSevenDays, lastThirtyDays, older } = groupedSessions;
  const hasAnySessions =
    today.length > 0 ||
    lastSevenDays.length > 0 ||
    lastThirtyDays.length > 0 ||
    older.length > 0;

  return (
    <SidebarContentWrapperOuter dataTest="Amelia chats" hasTabBar={false}>
      <Stack space="small">
        <NewChatItem
          label="New chat"
          iconName="examQuestion"
          onClick={() =>
            navigate(
              ROUTES.CLASSROOM.COURSE.ASK_AMELIA.CHAT.buildPath({
                courseId,
                sessionId: uuid()
              }),
              {
                state: ROUTES.CLASSROOM.COURSE.ASK_AMELIA.CHAT.buildState({
                  isNewSession: true
                })
              }
            )
          }
        />
        {hasAnySessions && (
          <SidebarContentWrapperInner>
            {isLoading ? (
              <LoadingSessions />
            ) : (
              <Sessions
                courseId={courseId}
                sessions={groupedSessions}
                activeSessionId={activeSessionId}
              />
            )}
          </SidebarContentWrapperInner>
        )}
      </Stack>
    </SidebarContentWrapperOuter>
  );
}
